'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { useGetBillingPlansQuery } from '@/api/user/organization/billing/get-billing-plans-query'
import { useGetOrganizationBillingQuery } from '@/api/user/organization/get-organization-billing-query'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { SettingsModal } from '@/features/settings-modal/settings-modal'
import {
    type ISettingsModalParamValue,
    type ISettingsOrganizationPage,
    type ISettingsTabId,
    formatSettingsModalValue,
    parseSettingsModalValue,
} from '@/setup/routing/settings-route'
import {
    MODAL_QUERY_PARAM_KEY,
    MODAL_TYPES,
    parseModalParam,
    formatModalParam,
    guardIsSettingsModal,
} from '@/setup/routing/modal-route'
import { useQueryParams } from '@/utils/url/use-query-params'
import { noOp } from '@/utils/functions/no-op'
import { usePlansEntrySourceZustand } from '@/features/subscription/billing/use-plans-entry-source-zustand'
import type { ConversionTriggerSource } from 'api/dist/types/posthog-events'

export type IOrganizationPage = ISettingsOrganizationPage

export interface ISettingsModalOptions {
    activeTab: ISettingsTabId
    organizationPage?: IOrganizationPage
    /** Plan type to focus on the Plans tab — selects its segment (Individual/Professional) and highlights it. */
    highlightedPlan?: string
    /**
     * WHICH CTA opened the Plans tab — stamped as the checkout's triggerSource for voluntary
     * (plans_page) conversions. Held in `usePlansEntrySourceZustand` (never the URL), so
     * deep-linked opens deliberately stay unattributed.
     */
    plansSource?: ConversionTriggerSource
}

const DEFAULT_OPTIONS: ISettingsModalOptions = {
    activeTab: 'plans',
    organizationPage: undefined,
}

type IOrganizationBillingData = ReturnType<typeof useGetOrganizationBillingQuery>['data']
type IBillingPlansData = ReturnType<typeof useGetBillingPlansQuery>['data']

export interface ISettingsModalContext {
    // Modal state
    isOpen: boolean
    openSettingsModal: (options?: Partial<ISettingsModalOptions>) => void
    closeSettingsModal: () => void
    // Options
    options: ISettingsModalOptions
    setOptions: (options: Partial<ISettingsModalOptions>) => void
    // Shared billing data
    organizationId: string | undefined
    billingAndUsage: IOrganizationBillingData
    billingPlans: IBillingPlansData
}

const SettingsModalContext = createContext<ISettingsModalContext>({
    isOpen: false,
    openSettingsModal: noOp,
    closeSettingsModal: noOp,
    options: DEFAULT_OPTIONS,
    setOptions: noOp,
    organizationId: undefined,
    billingAndUsage: undefined,
    billingPlans: undefined,
})

interface ISettingsModalProviderProps {
    children: ReactNode
}

export function SettingsModalProvider({ children }: ISettingsModalProviderProps) {
    const { params, getParam, setParam, deleteParam } = useQueryParams<{ [MODAL_QUERY_PARAM_KEY]: string }>()

    /** Reactive: the settings tab (+ optional sub-page) from the URL, or null if not a settings modal */
    const settingsInUrl = useMemo((): ISettingsModalParamValue | null => {
        const parsed = parseModalParam(params[MODAL_QUERY_PARAM_KEY])
        if (guardIsSettingsModal(parsed)) {
            return parseSettingsModalValue(parsed.value)
        }
        return null
    }, [params])

    const [isOpen, setIsOpen] = useState(() => {
        const parsed = parseModalParam(getParam(MODAL_QUERY_PARAM_KEY))
        return !!(guardIsSettingsModal(parsed) && parseSettingsModalValue(parsed.value))
    })
    const [options, setOptionsState] = useState<ISettingsModalOptions>(() => {
        const parsed = parseModalParam(getParam(MODAL_QUERY_PARAM_KEY))
        const settings = guardIsSettingsModal(parsed) ? parseSettingsModalValue(parsed.value) : null
        if (settings) {
            return { ...DEFAULT_OPTIONS, activeTab: settings.tabId, organizationPage: settings.organizationPage }
        }
        return DEFAULT_OPTIONS
    })

    // Sync from URL reactively (handles deep links, back/forward, redirects). This must also run
    // while the modal is already open: an email deep link or browser history navigation may need
    // to move an open Workspace modal from General to Members.
    useEffect(() => {
        if (settingsInUrl) {
            setIsOpen(true)
            setOptionsState((prev) => ({
                ...prev,
                activeTab: settingsInUrl.tabId,
                organizationPage: settingsInUrl.organizationPage,
            }))
        }
    }, [settingsInUrl])

    // Billing data fetched once, shared across billing tabs
    const organizationId = useOrganizationId()
    const { data: billingAndUsage } = useGetOrganizationBillingQuery(organizationId)
    const { data: billingPlans } = useGetBillingPlansQuery()

    const setOptions = useCallback(
        (opts: Partial<ISettingsModalOptions>) => {
            const { plansSource, ...restOpts } = opts
            const next = { ...options, ...restOpts }
            // highlightedPlan is only meaningful on the Plans tab; drop it when navigating elsewhere.
            if (next.activeTab !== 'plans') next.highlightedPlan = undefined
            // Attribution follows the navigation: an entry names its CTA or stays unattributed.
            usePlansEntrySourceZustand.getState().setPlansSource(next.activeTab === 'plans' ? plansSource : undefined)
            setOptionsState(next)
            setParam(
                MODAL_QUERY_PARAM_KEY,
                formatModalParam(
                    MODAL_TYPES.Settings,
                    formatSettingsModalValue({
                        tabId: next.activeTab,
                        organizationPage: next.organizationPage,
                    })
                )
            )
        },
        [options, setParam]
    )

    const openSettingsModal = useCallback(
        (opts?: Partial<ISettingsModalOptions>) => {
            const { plansSource, ...restOpts } = opts ?? {}
            const resolvedOptions = { ...DEFAULT_OPTIONS, ...restOpts }

            usePlansEntrySourceZustand
                .getState()
                .setPlansSource(resolvedOptions.activeTab === 'plans' ? plansSource : undefined)
            setOptionsState(resolvedOptions)
            setIsOpen(true)
            setParam(
                MODAL_QUERY_PARAM_KEY,
                formatModalParam(
                    MODAL_TYPES.Settings,
                    formatSettingsModalValue({
                        tabId: resolvedOptions.activeTab,
                        organizationPage: resolvedOptions.organizationPage,
                    })
                )
            )
        },
        [setParam]
    )

    const closeSettingsModal = useCallback(() => {
        setIsOpen(false)
        // A later URL-driven reopen must not inherit this episode's CTA attribution.
        usePlansEntrySourceZustand.getState().setPlansSource(undefined)
        deleteParam(MODAL_QUERY_PARAM_KEY)
        // Clear Clerk's hash routing when closing the modal
        if (window.location.hash.startsWith('#/organization-')) {
            queueMicrotask(() => {
                history.replaceState(null, '', window.location.pathname + window.location.search)
            })
        }
    }, [deleteParam])

    return (
        <SettingsModalContext.Provider
            value={{
                isOpen,
                openSettingsModal,
                closeSettingsModal,
                options,
                setOptions,
                organizationId,
                billingAndUsage,
                billingPlans,
            }}
        >
            {children}
            <SettingsModal />
        </SettingsModalContext.Provider>
    )
}

export function useSettingsModal() {
    return useContext(SettingsModalContext)
}
